import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, UserX, UserCheck } from 'lucide-react';
import Layout from '../../components/Layout';
import { userService, UserDTO } from '../../services/userService';
import { parseApiError } from '../../utils/errorUtils';
import '../../pages/Products/ProductCategoryDeletePage.css';

const UserDeactivatePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState<UserDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadUser = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getUserById(id || '0');
      if (response?.data?.data) {
        setUser(response.data.data);
      }
      setLoading(false);
    } catch {
      setError(t('user.failedToLoad'));
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!user) return;
    setIsSubmitting(true);
    try {
      if (user.isActive) {
        await userService.deactivateUser(user.id);
        navigate('/users', { state: { message: t('user.userDeactivatedSuccess') } });
      } else {
        await userService.activateUser(user.id);
        navigate('/users', { state: { message: t('user.userActivatedSuccess') } });
      }
    } catch (err: unknown) {
      const msg = user.isActive ? t('user.failedToDeactivate') : t('user.failedToActivate');
      setError(parseApiError(err, msg).message);
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{error || t('user.userNotFound')}</div>
        </div>
      </Layout>
    );
  }

  const isDeactivating = user.isActive;
  const pageTitle = isDeactivating ? t('user.deactivateTitle') : t('user.activateTitle');
  const confirmMessage = isDeactivating
    ? t('user.deactivateConfirmMessage')
    : t('user.activateConfirmMessage');
  const warningText = isDeactivating
    ? t('user.deactivateWarning')
    : t('user.activateWarning');
  const confirmButtonText = isDeactivating
    ? t('user.confirmDeactivate')
    : t('user.confirmActivate');

  return (
    <Layout>
      <div className="delete-container">
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={() => navigate(`/users/${id}`)}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('user.backToUser')}
          </button>
        </nav>

        <div className="delete-content">
          <div className="delete-card delete-card-main">
            <div
              className="delete-card-header"
              style={isDeactivating
                ? {}
                : { background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)', borderColor: '#6ee7b7' }}
            >
              {isDeactivating ? (
                <UserX size={24} strokeWidth={2} className="delete-icon" />
              ) : (
                <UserCheck size={24} strokeWidth={2} className="delete-icon" style={{ color: '#059669' }} />
              )}
              <h2 className="delete-card-title">{pageTitle}</h2>
            </div>

            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">{confirmMessage}</p>
                <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '8px' }}>{warningText}</p>
              </div>

              <div className="delete-category-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('user.username')}</label>
                  <p className="delete-info-value delete-code">{user.username}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('user.email')}</label>
                  <p className="delete-info-value">{user.email}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('user.fullName')}</label>
                  <p className="delete-info-value">{user.firstName} {user.lastName}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('user.status')}</label>
                  <p className="delete-info-value">
                    <span className={`badge ${user.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {user.isActive ? t('user.active') : t('user.inactive')}
                    </span>
                  </p>
                </div>
              </div>

              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={() => navigate(`/users/${id}`)}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className={`delete-btn ${isDeactivating ? 'delete-btn-confirm' : 'delete-btn-activate'}`}
                  style={!isDeactivating ? {
                    background: 'linear-gradient(135deg, #059669, #10b981)',
                    borderColor: '#059669',
                    color: '#fff',
                  } : {}}
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.saving')}
                    </>
                  ) : (
                    <>
                      {isDeactivating ? (
                        <UserX size={16} strokeWidth={2} />
                      ) : (
                        <UserCheck size={16} strokeWidth={2} />
                      )}
                      {confirmButtonText}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="delete-sidebar">
            <div className="delete-card">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('user.safetyInformation')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-help-section">
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('user.permanentAction')}</strong>
                    <p>{t('user.deactivateNote')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('user.relatedData')}</strong>
                    <p>{t('user.deactivateDataNote')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UserDeactivatePage;
